import { Building2, CreditCard, FolderKanban, Users } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorState, Loading, PageHeader } from "@/components/States";
import BetaPanel from "@/components/platform/BetaPanel";
import BetaInbox from "@/components/platform/BetaInbox";

const STATUS = { trialing: ["Trial", "secondary"], active: ["Active", "default"], past_due: ["Past due", "destructive"], expired: ["Trial ended", "destructive"], canceled: ["Canceled", "outline"] };

const Stat = ({ icon: Icon, label, value, testid, i }) => (
  <div className="rise rounded-xl border bg-card p-6" style={{ animationDelay: `${i * 70}ms` }}>
    <p className="eyebrow flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{label}</p>
    <p className="mt-4 font-sans text-4xl font-semibold leading-none tracking-tight" data-testid={testid}>{value}</p>
  </div>
);

function Row({ w }) {
  const [label, variant] = STATUS[w.status] || [w.status, "outline"];
  return (
    <tr className="border-b last:border-0" data-testid={`platform-row-${w.slug}`}>
      <td className="px-4 py-3"><p className="font-medium">{w.name}</p><p className="text-xs text-muted-foreground">{w.slug} · {w.owner_email || "no owner"}</p></td>
      <td className="px-4 py-3">{w.plan_name}{w.complimentary && <span className="ml-1 text-xs text-muted-foreground">(comp)</span>}</td>
      <td className="px-4 py-3"><Badge variant={variant}>{label}</Badge>{w.status === "trialing" && <span className="ml-2 text-xs text-muted-foreground">{w.trial_days_left}d left</span>}{w.cancel_at_period_end && <span className="ml-2 text-xs text-destructive">cancelling</span>}</td>
      <td className="px-4 py-3 text-right font-mono text-sm">{w.members}</td>
      <td className="px-4 py-3 text-right font-mono text-sm">{w.projects}</td>
      <td className="px-4 py-3 text-right font-mono text-sm" data-testid={`platform-mrr-${w.slug}`}>{w.mrr ? money(w.mrr) : "—"}</td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">{(w.created_at || "").slice(0, 10)}</td>
    </tr>
  );
}

export default function Platform() {
  const { data, error, loading, reload } = useFetch("/platform/overview");
  if (loading) return <div className="p-10"><Loading rows={3} /></div>;
  if (error) return <div className="p-10"><ErrorState message={error} onRetry={reload} /></div>;
  const t = data.totals;
  return (
    <div className="max-w-7xl space-y-10 p-6 md:p-10 lg:p-12" data-testid="platform-page">
      <PageHeader eyebrow="Platform owner" title="All workspaces"><Button asChild variant="outline" className="rounded-full"><Link to="/platform/pitch" data-testid="platform-pitch-link"><Presentation className="mr-2 h-4 w-4" />Pitch deck</Link></Button></PageHeader>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat i={0} icon={Building2} label="Workspaces" value={t.workspaces} testid="platform-stat-workspaces" />
        <Stat i={1} icon={CreditCard} label="Monthly revenue" value={money(t.mrr)} testid="platform-stat-mrr" />
        <Stat i={2} icon={Users} label="Trialing / Active" value={`${t.trialing} / ${t.active}`} testid="platform-stat-status" />
        <Stat i={3} icon={FolderKanban} label="People · Projects" value={`${t.members} · ${t.projects}`} testid="platform-stat-usage" />
      </div>
      <BetaPanel />
      <BetaInbox />
      <p className="text-sm text-muted-foreground">Annual run-rate {money(t.arr)}. Revenue counts paid subscriptions only (complimentary workspaces excluded); yearly plans are spread across 12 months.</p>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm" data-testid="platform-table">
          <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Workspace", "Plan", "Status", "People", "Projects", "MRR", "Created"].map((h, i) => <th key={h} className={`px-4 py-3 font-medium ${i >= 3 ? "text-right" : ""}`}>{h}</th>)}</tr>
          </thead>
          <tbody>{data.workspaces.map((w) => <Row key={w.id} w={w} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
